<script setup lang="ts">
const route = useRoute();
const queryTerm = route.query.q ? (route.query.q as string) : '';

const searchTerm = ref<string>(queryTerm);
const searchQuery = ref<string>(queryTerm);
const searchInput = ref<HTMLInputElement | null>(null);
const currentPage = ref(1);
const pageSize = 10;

const popularLinks = [
  'Academic Calendar',
  'Athletics',
  'Career Services',
  'Financial Aid',
  'Housing',
  'Programs',
  'Scholarships',
];

const exploreCards = [
  {
    title: 'Explore by area of study',
    description: 'Start with the subjects that fit your goals and see where they can lead.',
    links: [
      { label: 'Artificial Intelligence', href: '/programs/area-of-study/artificial-intelligence/' },
      { label: 'Arts and Humanities', href: '/programs/area-of-study/arts-and-humanities/' },
      { label: 'Business', href: '/programs/area-of-study/business/' },
      { label: 'Education and Teaching', href: '/programs/area-of-study/education-and-teaching/' },
      {
        label: 'Health and Healthcare Management',
        href: '/programs/area-of-study/health-and-healthcare-management/',
      },
      {
        label: 'Psychology and Social Services',
        href: '/programs/area-of-study/psychology-and-social-sciences/',
      },
      { label: 'Public and Human Services', href: '/programs/area-of-study/public-and-human-services/' },
      {
        label: 'Science, Math, and Technology',
        href: '/programs/area-of-study/science-math-and-technology/',
      },
    ],
  },
  {
    title: 'Explore by level',
    description: 'Compare programs by degree level so you can narrow your best next step quickly.',
    links: [
      { label: "Bachelor's Degrees", href: '/programs/bachelors-degrees/' },
      { label: "Master's Degrees", href: '/programs/masters-degrees/' },
      { label: 'Certificates', href: '/programs/certificates/' },
      { label: 'Associate Degrees', href: '/programs/associate-degrees/' },
      { label: 'Minors', href: '/programs/minor/' },
      { label: 'All Programs', href: '/programs/' },
    ],
  },
  {
    title: 'Popular resources',
    description: 'Jump straight to the pages students use most when planning their path.',
    links: [
      { label: 'Athletics', href: '/student-experience/athletics/' },
      { label: 'Financial Aid', href: '/tuition-and-aid/' },
      { label: 'Housing', href: '/student-experience/campus-life/student-housing/' },
      { label: 'Student Services', href: '/student-experience/student-services/' },
      { label: 'Transfer Credits', href: '/admissions/transfer-credits/' },
    ],
  },
];

function updateTerm() {
  searchTerm.value = searchQuery.value;
  history.replaceState(history.state, '', `?q=${encodeURIComponent(searchQuery.value)}`);
}

function searchPopular(term: string) {
  searchQuery.value = term;
  updateTerm();
}

function goToPage(page: number) {
  currentPage.value = page;
}

function goToPreviousPage() {
  if (currentPage.value > 1) {
    currentPage.value -= 1;
  }
}

function goToNextPage() {
  if (currentPage.value < totalPages.value) {
    currentPage.value += 1;
  }
}

onMounted(() => {
  searchInput.value?.focus();
});

const { data, pending, refresh, status, error } = await useAsyncData(
  () => {
    if (searchTerm.value.length === 0) {
      return Promise.resolve({ results: [] });
    } else {
      return $fetch<any>('/api/search/?q', {
        params: { q: searchTerm.value },
      });
    }
  },
  {
    watch: [searchTerm],
    server: false,
  },
);

const allResults = computed(() => data.value?.results || []);
const totalPages = computed(() => Math.max(1, Math.ceil(allResults.value.length / pageSize)));
const paginatedResults = computed(() => {
  const start = (currentPage.value - 1) * pageSize;
  return allResults.value.slice(start, start + pageSize);
});
const displayedPages = computed<(number | 'ellipsis')[]>(() => {
  const total = totalPages.value;
  const current = currentPage.value;

  if (total <= 7) {
    return Array.from({ length: total }, (_, index) => index + 1);
  }

  if (current <= 3) {
    return [1, 2, 3, 4, 'ellipsis', total];
  }

  if (current >= total - 2) {
    return [1, 'ellipsis', total - 3, total - 2, total - 1, total];
  }

  return [1, 'ellipsis', current - 1, current, current + 1, 'ellipsis', total];
});

const mobileDisplayedPages = computed<(number | 'ellipsis')[]>(() => {
  const total = totalPages.value;
  const current = currentPage.value;

  if (total <= 4) {
    return Array.from({ length: total }, (_, index) => index + 1);
  }

  if (current <= 2) {
    return [1, 2, 'ellipsis', total];
  }

  if (current >= total - 1) {
    return [1, 'ellipsis', total - 1, total];
  }

  return [1, 'ellipsis', current, 'ellipsis', total];
});

watch(searchTerm, () => {
  currentPage.value = 1;
});

watch(totalPages, (pageCount) => {
  if (currentPage.value > pageCount) {
    currentPage.value = pageCount;
  }
});
</script>

<template>
  <div class="bg-accent-25 py-10 md:pt-16 md:pb-12">
    <LayoutsContainer>
      <TypographyTitle as="h1" size="lg" class="text-brand-600 max-w-[18ch]">
        find what you're looking for.
      </TypographyTitle>
      <p class="text-brand-700 mt-4 max-w-2xl text-base leading-relaxed md:text-lg">
        Search programs, admissions, financial aid, and campus resources all in one place.
      </p>

      <div class="mt-8 max-w-3xl">
        <form class="space-y-4" @submit.prevent="updateTerm">
          <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
            <label for="search" class="sr-only">Search</label>
            <div class="relative w-full">
              <Icon
                name="icon:search"
                class="text-brand-500 pointer-events-none absolute top-1/2 left-4 !size-5 -translate-y-1/2"
              />
              <input
                ref="searchInput"
                id="search"
                v-model="searchQuery"
                type="text"
                class="border-brand-500 w-full rounded-lg border bg-white py-3 pr-4 pl-12 text-base focus:ring-0 focus:outline-none"
                placeholder='Try "MBA online", "financial aid", or "transfer credits"'
              />
            </div>
            <button
              type="submit"
              class="bg-brand-600 hover:bg-brand-500 inline-flex shrink-0 cursor-pointer items-center justify-center rounded-lg px-6 py-3 font-bold text-white transition-colors duration-200"
            >
              Search
            </button>
          </div>

          <div class="flex flex-wrap items-center gap-2">
            <span class="text-brand-700 mr-1 text-sm font-bold">Popular:</span>
            <button
              v-for="link in popularLinks"
              :key="link"
              type="button"
              @click="searchPopular(link)"
              class="bg-brand-50 text-brand-700 hover:bg-brand-200/30 inline-flex cursor-pointer rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium transition-colors"
            >
              {{ link }}
            </button>
          </div>
        </form>
      </div>

      <div
        v-if="error || pending || allResults.length > 0 || (searchTerm.length > 0 && status !== 'idle')"
        class="mt-8"
      >
        <div v-if="error">We have encountered an error searching. Please try again.</div>
        <div v-else-if="pending">Searching...</div>
        <div v-else-if="allResults.length > 0">
          <ul>
            <li
              class="border-b-brand-200 border-b-1 last:border-b-0"
              v-for="result in paginatedResults"
              :key="result.id"
            >
              <a class="group flex items-end justify-between py-5" :href="withTrailingSlash(result.link)">
                <span>
                  <span class="text-brand-700 mb-1 block text-xs uppercase">{{ result.type }}</span>
                  {{ result.title }}
                </span>
                <span class="text-brand-500">
                  <Icon
                    name="icon:arrow-right"
                    class="block -translate-x-2 opacity-0 transition-all duration-75 group-hover:translate-x-0 group-hover:opacity-100"
                  />
                </span>
              </a>
            </li>
          </ul>

          <div v-if="totalPages > 1" class="mt-8 flex items-center justify-center gap-2 sm:gap-6">
            <button
              type="button"
              class="border-brand-500 text-brand-600 inline-flex cursor-pointer items-center justify-center rounded-lg border px-2 py-2 text-xs font-bold transition-colors disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-300 sm:px-4 sm:text-sm"
              :disabled="currentPage === 1"
              @click="goToPreviousPage"
            >
              <span class="sm:hidden">Prev</span>
              <span class="hidden sm:inline">Previous</span>
            </button>

            <div class="mx-1 flex items-center justify-center gap-1.5 sm:hidden">
              <template v-for="(page, index) in mobileDisplayedPages" :key="`mobile-${page}-${index}`">
                <span
                  v-if="page === 'ellipsis'"
                  class="text-brand-600 inline-flex min-w-6 items-center justify-center px-1 text-xs font-bold"
                  aria-hidden="true"
                >
                  ...
                </span>
                <button
                  v-else
                  type="button"
                  class="border-brand-500 inline-flex min-w-7 cursor-pointer items-center justify-center rounded-lg border px-2 py-1.5 text-xs font-bold transition-colors"
                  :class="
                    page === currentPage ? 'bg-brand-600 text-white' : 'text-brand-600 hover:bg-brand-50 bg-white'
                  "
                  :aria-current="page === currentPage ? 'page' : undefined"
                  @click="goToPage(page)"
                >
                  {{ page }}
                </button>
              </template>
            </div>

            <div class="mx-1 hidden items-center justify-center gap-1.5 sm:mx-2 sm:flex sm:gap-2">
              <template v-for="(page, index) in displayedPages" :key="`${page}-${index}`">
                <span
                  v-if="page === 'ellipsis'"
                  class="text-brand-600 inline-flex min-w-8 items-center justify-center px-1 text-sm font-bold"
                  aria-hidden="true"
                >
                  ...
                </span>
                <button
                  v-else
                  type="button"
                  class="border-brand-500 inline-flex min-w-8 cursor-pointer items-center justify-center rounded-lg border px-2 py-2 text-sm font-bold transition-colors sm:min-w-10 sm:px-3"
                  :class="
                    page === currentPage ? 'bg-brand-600 text-white' : 'text-brand-600 hover:bg-brand-50 bg-white'
                  "
                  :aria-current="page === currentPage ? 'page' : undefined"
                  @click="goToPage(page)"
                >
                  {{ page }}
                </button>
              </template>
            </div>

            <button
              type="button"
              class="border-brand-500 text-brand-600 inline-flex cursor-pointer items-center justify-center rounded-lg border px-2 py-2 text-xs font-bold transition-colors disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-300 sm:px-4 sm:text-sm"
              :disabled="currentPage === totalPages"
              @click="goToNextPage"
            >
              Next
            </button>
          </div>
        </div>
        <div v-else-if="searchTerm.length > 0 && status !== 'idle'">No results found.</div>
      </div>
    </LayoutsContainer>
  </div>

  <div class="bg-brand-600 py-10 md:py-12">
    <LayoutsContainer>
      <section class="px-0">
        <TypographyTitle as="h2" size="lg" class="text-center text-white"
          >explore a few places to begin.</TypographyTitle
        >
        <p class="mx-auto mt-4 max-w-3xl text-center text-sm leading-relaxed text-white md:text-base">
          Browse by subject, find the right degree level, or jump straight to the resources students ask for most.
        </p>

        <div class="mt-8 grid gap-8 md:grid-cols-2 xl:grid-cols-3">
          <article
            v-for="card in exploreCards"
            :key="card.title"
            class="rounded-2xl bg-white px-6 pt-8 pb-6"
            style="box-shadow: 6px 10px 0 0 rgba(245, 240, 252, 0.75)"
          >
            <h3 class="text-brand-600 text-xl leading-tight font-extrabold">{{ card.title }}</h3>
            <p class="text-brand-700 mt-3 text-sm leading-relaxed">{{ card.description }}</p>
            <hr class="bg-brand-200 mt-5 mb-4 h-px border-0" />
            <ul class="space-y-2">
              <li v-for="link in card.links" :key="link.href">
                <a
                  :href="link.href"
                  class="text-brand-600 hover:text-brand-500 text-sm font-bold transition-colors hover:underline"
                >
                  {{ link.label }}
                </a>
              </li>
            </ul>
          </article>
        </div>
      </section>
    </LayoutsContainer>
  </div>
</template>
